import { useMemo, useState } from 'react';
import { owedOfframps, ApiClientError, type OwedOfframp } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatDateTime } from '../lib/format';
import { Alert, Badge, Card, Copyable, Empty, Spinner } from '../components/ui';

const TX = 'https://basescan.org/tx/';
const ADDR = 'https://basescan.org/address/';
const short = (h: string) => `${h.slice(0, 8)}…${h.slice(-6)}`;

/**
 * Offramps that received a crypto deposit but were never paid. The treasurer
 * verifies each on-chain (deposit + settlement tx links) and clicks Pay — the
 * payout is guarded server-side (one payout per order, can't double-pay).
 */
export function OwedOfframps() {
  const res = useResource(() => owedOfframps.list(), [], { refreshMs: 30000 });
  const [paying, setPaying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [q, setQ] = useState('');

  async function pay(orderId: string, name: string | null) {
    if (!confirm(`Pay ${name ?? 'this customer'}? This sends the NGN payout now.`)) return;
    setError(null);
    setOk(null);
    setPaying(orderId);
    try {
      const r = await owedOfframps.pay(orderId);
      setOk(`Paid ${name ?? orderId}: ₦${r.owed_ngn}`);
      await res.reload();
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Payout failed');
    } finally {
      setPaying(null);
    }
  }

  const owed = useMemo(() => {
    const rows = (res.data ?? []).filter((r) => !r.already_paid);
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    const hit = (o: OwedOfframp) =>
      [
        o.recipient_name,
        o.destination_account,
        o.order_id,
        o.owed_ngn,
        o.settled_usdc,
        o.deposit_address,
        o.origin_tx_hash,
        o.settlement_tx_hash,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    return rows.filter(hit);
  }, [res.data, q]);

  return (
    <>
      <div className="page-header">
        <h1>Owed offramps</h1>
        <p>
          Offramps where a crypto deposit landed but no NGN was paid. Verify each on-chain, then
          pay it — we hold the USDC; the customer is owed their NGN.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}
      {ok && <Alert tone="success">{ok}</Alert>}

      <Card title={`Owed customers${owed.length ? ` (${owed.length})` : ''}`}>
        <div style={{ marginBottom: 12, maxWidth: 360 }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, account, amount, tx hash, address…"
            style={{ width: '100%' }}
          />
        </div>

        {res.loading && !res.data ? (
          <Spinner />
        ) : !owed.length ? (
          <Empty>{q ? 'No matches.' : 'No owed offramps — all settled. ✓'}</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Bank account</th>
                  <th>USDC</th>
                  <th>Owed (NGN)</th>
                  <th>Reason</th>
                  <th>Deposit address</th>
                  <th>Deposit tx</th>
                  <th>Settlement tx</th>
                  <th>Order</th>
                  <th>When</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {owed.map((r) => (
                  <tr key={r.order_id}>
                    <td>{r.recipient_name ?? '—'}</td>
                    <td className="mono">{r.destination_account}</td>
                    <td className="mono">{r.settled_usdc ?? '—'}</td>
                    <td className="mono">{r.owed_ngn ? `₦${r.owed_ngn}` : '—'}</td>
                    <td>
                      <Badge tone={r.finding === 'deposit_completed' ? 'danger' : 'warning'}>
                        {r.finding === 'deposit_completed' ? 'settled, unpaid' : 'refunded to us'}
                      </Badge>
                    </td>
                    <td>
                      {r.deposit_address ? (
                        <Copyable
                          value={r.deposit_address}
                          display={short(r.deposit_address)}
                          href={ADDR + r.deposit_address}
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {r.origin_tx_hash ? (
                        <Copyable
                          value={r.origin_tx_hash}
                          display={short(r.origin_tx_hash)}
                          href={TX + r.origin_tx_hash}
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {r.settlement_tx_hash ? (
                        <Copyable
                          value={r.settlement_tx_hash}
                          display={short(r.settlement_tx_hash)}
                          href={TX + r.settlement_tx_hash}
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <Copyable value={r.order_id} display={r.order_id.slice(0, 8)} />
                    </td>
                    <td className="muted">{formatDateTime(r.created_at)}</td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={paying === r.order_id || !r.owed_ngn}
                        onClick={() => pay(r.order_id, r.recipient_name)}
                      >
                        {paying === r.order_id ? 'Paying…' : 'Pay'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
          Each payout is one-per-order and re-checked before sending, so it can't double-pay. Rows
          disappear once paid.
        </p>
      </Card>
    </>
  );
}
